import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  scheduleVisits: PropTypes.array,
};

const defaultProps = {
  title: '',
  scheduleVisits: [],
};

@autobind
class TableInvoiceVisits extends BaseComponent {
  constructor(props) {
    super(props);
  }

  createColumns() {
    return [{
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Date',
      dataIndex: 'dateUsString',
      sorter: (a, b) => a.date - b.date,
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Service Code',
      dataIndex: 'serviceCode',
      sorter: (a, b) => a.serviceCode?.localeCompare(b.serviceCode),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientId - b.patientId,
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      sorter: (a, b) => a.caregiverId - b.caregiverId,
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/caregivers/caregiver_profile?id=${record.caregiverId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Sch. Times',
      dataIndex: 'scheduleTimes',
      sorter: (a, b) => a.fromTime - b.fromTime,
    }, {
      title: 'Visit Times',
      dataIndex: 'visitTimes',
      sorter: (a, b) => a.visitIn - b.visitIn,
    }, {
      title: 'Bill Hours',
      dataIndex: 'billHoursString',
      sorter: (a, b) => a.billHours - b.billHours,
    }, {
      title: 'Unit Type',
      dataIndex: 'unitType',
      sorter: (a, b) => a.unitType?.localeCompare(b.unitType),
    }, {
      title: 'Unit Rate',
      dataIndex: 'unitRateString',
      sorter: (a, b) => a.unitRate - b.unitRate,
    }, {
      title: 'Amount',
      dataIndex: 'amountString',
      sorter: (a, b) => a.amount - b.amount,
    }];
  }

  createDataSource(scheduleVisits) {
    const dataSource = [];
    scheduleVisits.forEach((visit, index) => {
      dataSource.push({
        key: visit.getId(),
        id: visit.getId(),
        date: visit.getLong('date'),
        dateUsString: visit.getString('dateUsString'),
        companyName: visit.getString('companyName'),
        serviceCode: visit.getRemoteObject('service')?.getString('displayCode'),
        patientId: visit.getLong('patientId'),
        patientName: visit.getString('patientName'),
        caregiverId: visit.getLong('caregiverId'),
        caregiverName: visit.getString('caregiverName'),
        fromTime: visit.getLong('fromTime'),
        scheduleTimes: `${visit.getString('fromTimeString')}-${visit.getString('toTimeString')}`,
        visitIn: visit.getLong('visitIn'),
        visitTimes: `${visit.getString('visitInString') || ''}-${visit.getString('visitOutString') || ''}`,
        billHours: visit.getLong('billHours'),
        billHoursString: visit.getString('billHoursString'),
        unitType: visit.getRemoteObject('service')?.getString('unitType'),
        unitRate: visit.getRemoteObject('service')?.getDouble('unitRate'),
        unitRateString: helpers.toDollars(visit.getRemoteObject('service')?.getDouble('unitRate')),
        amount: visit.getDouble('amount'),
        amountString: helpers.toDollars(visit.getDouble('amount')),
      });
    });
    return dataSource;
  }

  renderContent() {
    const { scheduleVisits, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="yellow_gold"
        fontSize="12px"
        size="small"
        title={title}
        icon="check-square"
        columns={this.createColumns()}
        dataSource={this.createDataSource(scheduleVisits)}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableInvoiceVisits.propTypes = propTypes;
TableInvoiceVisits.defaultProps = defaultProps;

export default TableInvoiceVisits;
